<template>
  <div
    class="image-container"
    :class="{'fs': isFullScreen}"
    ref="container"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    >
    <img
      @click="goFull"
      :src="sauce"
      class="image"
      :class="{'currentImage': canFullScreen && !isFullScreen}"
      :style="isFullScreen ? zoomStyle : null"
      draggable="false"
      ref="image"
      />
  </div>
</template>

<script>
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 8;
  const DOUBLE_TAP_MS = 300;

  export default {
    name: 'Image',

    props: {
      sauce: String,
      canFullScreen: false,
    },
    data() {
      return {
        isFullScreen: false,
        //zoom state (only used in full screen)
        scale: 1,
        tx: 0,
        ty: 0,
      }
    },

    computed: {
      zoomStyle(){
        return {
          transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`,
          cursor: this.scale > 1 ? 'grab' : 'zoom-in',
        }
      },
    },

    methods: {
      goFull() {
        //clicks while in full screen are handled by the pointer events
        if (!this.canFullScreen || this.isFullScreen) return;

        this.isFullScreen = true;
        document.addEventListener('keydown', this.handleEscapeKey);
        //native full screen when available, otherwise the .fs class acts as an overlay (iPhone)
        const container = this.$refs.container;
        if (container.requestFullscreen) {
          container.requestFullscreen().catch(() => {});
        }
      },
      exitFull() {
        this.isFullScreen = false;
        this.resetZoom();
        document.removeEventListener('keydown', this.handleEscapeKey);
        if (document.fullscreenElement) {document.exitFullscreen()};
      },
      handleEscapeKey(event) {
        if (event.key === 'Escape') this.exitFull();
      },
      onFullscreenChange() {
        //browser left full screen by itself (esc key, back gesture...)
        if (!document.fullscreenElement && this.isFullScreen) this.exitFull();
      },

      // ---- zoom ----
      resetZoom() {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
      },
      //zoom to newScale keeping the screen point (x,y) still
      zoomAt(x, y, newScale) {
        newScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newScale));
        const rect = this.$refs.container.getBoundingClientRect();
        const px = x - (rect.left + rect.width/2);
        const py = y - (rect.top + rect.height/2);
        const ratio = newScale / this.scale;
        this.tx = px - ratio * (px - this.tx);
        this.ty = py - ratio * (py - this.ty);
        this.scale = newScale;
        this.clampPan();
      },
      //dont let the image be dragged away from the screen
      clampPan() {
        if (this.scale <= 1) {
          this.resetZoom();
          return;
        }
        const img = this.$refs.image;
        const rect = this.$refs.container.getBoundingClientRect();
        const maxX = Math.max(0, (img.offsetWidth  * this.scale - rect.width)  / 2);
        const maxY = Math.max(0, (img.offsetHeight * this.scale - rect.height) / 2);
        this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
        this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
      },
      onWheel(event) {
        if (!this.isFullScreen) return;
        event.preventDefault();
        //dont let parents use the wheel too (carousel changes wall on wheel)
        event.stopPropagation();
        this.zoomAt(event.clientX, event.clientY, this.scale * Math.exp(-event.deltaY * 0.002));
      },

      // ---- mouse + touch (pointer events) ----
      onPointerDown(event) {
        if (!this.isFullScreen) return;
        this.$refs.container.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
        if (this.pointers.size === 1) this.moved = 0;
        if (this.pointers.size === 2) this.pinch = this.pinchInfo();
      },
      onPointerMove(event) {
        const prev = this.pointers.get(event.pointerId);
        if (!prev) return;
        const dx = event.clientX - prev.x;
        const dy = event.clientY - prev.y;
        this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
        this.moved += Math.abs(dx) + Math.abs(dy);

        if (this.pointers.size === 2) {
          //pinch zoom + pan with the two fingers
          const now = this.pinchInfo();
          this.tx += now.x - this.pinch.x;
          this.ty += now.y - this.pinch.y;
          this.zoomAt(now.x, now.y, this.scale * now.dist / this.pinch.dist);
          this.pinch = now;
        } else if (this.pointers.size === 1 && this.scale > 1) {
          //drag to move around the zoomed image
          this.tx += dx;
          this.ty += dy;
          this.clampPan();
        }
      },
      onPointerUp(event) {
        if (!this.pointers.has(event.pointerId)) return;
        const wasSingle = this.pointers.size === 1;
        this.pointers.delete(event.pointerId);
        if (!wasSingle || this.moved > 8) return;

        //it was a tap/click
        const now = Date.now();
        if (now - this.lastTap < DOUBLE_TAP_MS) {
          //double tap/click: toggle zoom on that point
          clearTimeout(this.exitTimer);
          this.lastTap = 0;
          if (this.scale > 1) this.resetZoom();
          else this.zoomAt(event.clientX, event.clientY, 2.5);
        } else {
          //single tap/click on an unzoomed image leaves full screen (like before)
          this.lastTap = now;
          this.exitTimer = setTimeout(() => {
            if (this.scale === 1) this.exitFull();
          }, DOUBLE_TAP_MS);
        }
      },
      pinchInfo() {
        const [a, b] = [...this.pointers.values()];
        return {
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
          dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        };
      },
    },

    created(){
      //gesture state, not reactive
      this.pointers = new Map();
      this.pinch = null;
      this.moved = 0;
      this.lastTap = 0;
      this.exitTimer = null;
    },
    mounted(){
      //wheel needs passive:false to block page scroll while zooming
      this.$refs.container.addEventListener('wheel', this.onWheel, {passive: false});
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
    },
    beforeUnmount(){
      clearTimeout(this.exitTimer);
      this.$refs.container.removeEventListener('wheel', this.onWheel);
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('keydown', this.handleEscapeKey);
      if (this.isFullScreen && document.fullscreenElement) {document.exitFullscreen()};
    },
  }
</script>

<style scoped>
  .image-container {
    position: relative;
    width: 100%;
    height: 100vh; /* Use the full viewport height */
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .image {
    max-width: 100%;
    max-height: 100%;
  }

  .currentImage:hover {
    transform: scale(1.02);
    cursor: zoom-in;
  }

  /* full screen (native or overlay fallback) */
  .image-container.fs {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    z-index: 9999;
    background-color: black;
    overflow: hidden;
    touch-action: none; /* we handle pinch/pan ourselves */
    user-select: none;
  }
  .fs .image {
    transform-origin: center center;
    will-change: transform;
  }
</style>
